import { CircularProgress } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useTheme } from '@material-ui/styles';
import { useDashboardAnalytics } from 'frontend/src/contexts/DashboardAnalytics';
import mapValues from 'lodash.mapvalues';
import sum from 'lodash.sum';
import React from 'react';
import { Cell, Pie, PieChart, Tooltip } from 'recharts';
import InfoIconWithTooltip from '../../../BuildingBlocks/InfoIconWithTooltip';
import Card from '../../Card';
import { RechartsTooltipProps } from '../types';
import { getAnalyticLabel } from '../utils';
import ChartLabel from './ChartLabel';
import RevenueChartTooltip from './RevenueChartTooltip';

const useStyles = makeStyles((theme: any) => ({
  cornerIcon: {
    position: 'absolute',
    top: theme.spacing(2.5),
    right: theme.spacing(2.25),
  },
  chartContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    position: 'relative',
  },
  labelsContiner: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: theme.spacing(3),
    justifyContent: 'center',
  },
}));

export default function RevenueBreakdownChart() {
  const classes = useStyles({});
  const theme: any = useTheme();
  const COLORS = theme.palette.analyticsColors;
  const { analytics, isLoading } = useDashboardAnalytics();
  const { revenues = {} } = analytics;
  const { projectedRevenueBreakdown = {} } = revenues;

  const totalIncome = sum(Object.values(projectedRevenueBreakdown));
  const percentageMap: Record<string, number> = mapValues(
    projectedRevenueBreakdown,
    (value) => (100 * value) / totalIncome,
  );
  const chartData = Object.entries(projectedRevenueBreakdown).map(
    ([name, value]) => ({ name, value }),
  );

  return (
    <Card title={'Forecasted Revenue Breakdown'} noPadding={true}>
      <div className={classes.cornerIcon}>
        <InfoIconWithTooltip helpText="This is an estimate of selling costs & profit as a percentage of revenue." />
      </div>
      {!isLoading && (
        <div className={classes.chartContainer}>
          <PieChart width={350} height={350}>
            <Pie
              data={chartData}
              cx={175}
              cy={175}
              labelLine={false}
              outerRadius={165}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip
              wrapperStyle={{
                height: '100%',
                width: '100%',
              }}
              content={(props: RechartsTooltipProps) => (
                <RevenueChartTooltip percentageMap={percentageMap} {...props} />
              )}
            />
          </PieChart>
          <div className={classes.labelsContiner}>
            {chartData.map((entry, index) => {
              const color = COLORS[index % COLORS.length];
              const name = getAnalyticLabel(entry.name);
              return <ChartLabel key={name} color={color} name={name} />;
            })}
          </div>
        </div>
      )}
      {isLoading && <CircularProgress />}
    </Card>
  );
}
